import { FadeIn } from "@/components/FadeIn";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useSignOutFully } from "../hooks/useSignOutFully";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useOrganization } from "../gen/rbt/cloud/v1alpha1/organization/organization_rbt_react";
import { User_Status } from "../gen/rbt/cloud/v1alpha1/user/user_pb";
import { User, useUser } from "../gen/rbt/cloud/v1alpha1/user/user_rbt_react";
import { useAuthenticatedSession } from "../hooks/useAuthenticatedSession";
import { CreateOrganization } from "./CreateOrganization";
import blueArc from "/blue-arc.svg";
import greenWedge from "/green-wedge.svg";

import rebootCloud from "/reboot-cloud.svg";

const Footer = () => {
  return (
    <div className="h-20">
      <Separator />
      <div className="flex justify-between h-full items-center text-sm">
        <div className="flex">
          {/* TODO(riley): add privacy policy and TOS.*/}
          <a href="https://reboot.dev" target="_blank" className="mr-10">
            Reboot, Planet Earth
          </a>
        </div>
        <div>© {new Date().getFullYear()} Reboot</div>
      </div>
    </div>
  );
};

const WaitlistHeader = ({
  userState,
  onSignOut,
}: {
  userState: User.State | undefined;
  onSignOut: () => void;
}) => {
  return (
    <header className="h-20 flex justify-between items-center mt-20">
      <img src={rebootCloud} className="h-10" />
      <div className="flex items-center">
        <div className="mr-6">
          Status:{" "}
          <span className="font-bold">
            {userState?.status == 0 ? "Waitlisted" : "Active"}
          </span>
        </div>
        <Sheet>
          <SheetTrigger>
            <Avatar className="cursor-pointer">
              <AvatarFallback className="bg-primary text-secondary font-bold">
                {userState?.email?.[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </SheetTrigger>
          <SheetContent>
            <SheetTitle className="sr-only">User Menu</SheetTitle>
            <SheetDescription className="sr-only">
              View your account details and logout
            </SheetDescription>
            <div className="flex flex-col gap-4 mt-6">
              <Button variant="secondary" onClick={onSignOut}>
                Logout
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="w-screen min-w-96 flex justify-center items-center">
      <div className="max-w-7xl w-screen mx-10 flex flex-col h-screen justify-between">
        {children}
      </div>
    </div>
  );
};

interface ButtonLinkCardProps {
  title: string;
  description: string;
  buttonText: string;
  linkAddress: string;
}

const ButtonLinkCard = ({
  title,
  description,
  buttonText,
  linkAddress,
}: ButtonLinkCardProps) => {
  return (
    <div className="relative w-64 h-36">
      <div className="text-lg font-bold my-2">{title}</div>
      <div className="font-thin my-2">{description}</div>
      <div className="absolute bottom-0">
        <Button asChild variant="secondary" className="w-48">
          <a href={linkAddress} target="_blank">
            {buttonText}
          </a>
        </Button>
      </div>
    </div>
  );
};

const Body = ({ children }: { children: React.ReactNode }) => {
  return <div className="mb-auto h-10">{children}</div>;
};

// Checks if the organization's subscription has a payment
// method. Redirects to the main app if so, otherwise renders
// the payment form.
function PaymentGate({ organizationId }: { organizationId: string }) {
  const navigate = useNavigate();
  const { useSubscription, useGet } = useOrganization({
    id: organizationId,
  });
  const { response: subResponse } = useSubscription();
  const { response: getResponse } = useGet();

  useEffect(() => {
    // Billing-exempt organizations have no payment method to collect;
    // send their members straight into the app.
    if (
      subResponse?.billingExempt ||
      subResponse?.subscription?.paymentMethodId
    ) {
      navigate("/", { replace: true });
    }
  }, [subResponse, navigate]);

  // Still loading.
  if (!subResponse || !getResponse) {
    return <></>;
  }

  // Billing-exempt or payment complete — redirect will happen from
  // the effect.
  if (subResponse.billingExempt || subResponse.subscription?.paymentMethodId) {
    return <></>;
  }

  // Payment not yet complete — show the payment form.
  return (
    <CreateOrganization
      existingOrganizationId={organizationId}
      existingOrganizationName={getResponse.name}
      onOrganizationCreated={() => navigate("/", { replace: true })}
    />
  );
}

export const LoggedInDashboard = ({ userId }: { userId: string }) => {
  const { useGet } = useUser({ id: userId });
  const { response } = useGet();
  const navigate = useNavigate();
  const signOut = useSignOutFully();

  if (response == undefined) {
    return <></>;
  }

  const userState = response.userState;

  // Prefer the last-selected org (set during org creation),
  // falling back to the first org in the list.
  const currentOrgId =
    userState?.lastSelectedOrganizationId || userState?.organizationIds[0];

  // User is active and has an org — verify payment before
  // redirecting.
  if (userState?.status === User_Status.ACTIVE && currentOrgId) {
    return <PaymentGate organizationId={currentOrgId} />;
  }

  // User is active but has no organization - show create organization
  // screen.
  if (
    userState?.status === User_Status.ACTIVE &&
    userState?.organizationIds.length === 0
  ) {
    return (
      <CreateOrganization
        onOrganizationCreated={() => navigate("/", { replace: true })}
      />
    );
  }

  // User is on the waitlist - show waitlist dashboard.
  return (
    <FadeIn durationMilliseconds={1000}>
      <Layout>
        <WaitlistHeader
          userState={response.userState}
          onSignOut={() => void signOut()}
        />
        <Body>
          <p className="w-6/12 text-xl my-10">
            Welcome to Reboot Cloud. You are on the waitlist.
            <br></br>
            <span className="text-lg">
              In the meantime, check out these resources to get started with
              Reboot:
            </span>
          </p>
          <img src={greenWedge} className="absolute left-0" />
          <div className="flex justify-center items-center">
            <div className="grid grid-cols-2 gap-16">
              <ButtonLinkCard
                title="Quick start guide"
                description="Get started with Reboot in under 15 minutes!"
                buttonText="TypeScript quickstart"
                linkAddress="https://docs.reboot.dev/full_stack_apps/typescript"
              />
              <ButtonLinkCard
                title="Discord channel"
                description="Have questions? Feedback? Chat with Reboot developers."
                buttonText="Join the conversation"
                linkAddress="https://discord.gg/cRbdcS94Nr"
              />
              <ButtonLinkCard
                title="Reboot docs"
                description="Everything you need to learn in one place."
                buttonText="Explore Reboot"
                linkAddress="https://docs.reboot.dev"
              />
            </div>
          </div>
        </Body>
        <div className="w-full flex flex-row-reverse">
          <img src={blueArc} className="w-36 my-4 mr-10" />
        </div>
        <Footer />
      </Layout>
    </FadeIn>
  );
};

export function Dashboard() {
  const session = useAuthenticatedSession();

  if (!session) {
    return <></>;
  }

  return <LoggedInDashboard {...session} />;
}
